import { OffersInterface, OffersInterfaceWithId, OfferType } from "./interfaces/offers.interface";
import { UserDataInterfaceWithId } from "./interfaces/userData.interface";

export const FIREBASE_IN_OPERATOR_MAX_ELEMENTS_SIZE = 10;

// roles
export const distributorRole = 'distributor' as const; // main role
export const ambassadorRole = 'ambassador' as const; // main role
export const demoRole = 'demo' as const; // main role
export const corporateAssociateRole = 'corporate_associate' as const; // main role
export const onpremiseRole = 'onpremise' as const; // sub role of distributor
export const offpremiseRole = 'offpremise' as const; // sub role of distributor
export const hybridRole = 'hybrid' as const; // sub role of distributor
export const activeRoles = [
  distributorRole,
  ambassadorRole,
  demoRole,
  onpremiseRole,
  offpremiseRole,
  hybridRole,
  corporateAssociateRole,
] as const;
export type ActiveJobType = typeof activeRoles[number];

export const mainRoles = [distributorRole, ambassadorRole, demoRole, corporateAssociateRole] as const;
export const subRoles = [onpremiseRole, offpremiseRole, hybridRole] as const;

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const getRewardAmountByRole = (offer: OffersInterfaceWithId, role: ActiveJobType): number => {
  const rewardAmount = offer?.rewardAmount || 0;

  return rewardAmount ? rewardAmount :
    activeRoles.includes(role) && offer?.[`${role}RewardAmount`] ?
      offer?.[`${role}RewardAmount`]
      : rewardAmount; // 0
}

export type rewardAmountRole = {
  [K in ActiveJobType as `rewardAmount${Capitalize<K>}`]?: number | null;
};

export type roleRewardAmount = {
  [K in ActiveJobType as `${K}RewardAmount`]?: number | null;
};

export type bonusRoleRewardAmount = {
  [K in ActiveJobType as `bonus${Capitalize<K>}RewardAmount`]?: number | null;
};

export type roleOfferFee = {
  [K in ActiveJobType as `${K}OfferFee`]?: number | null;
};

/* 
For distributor role: (https://leafvip.slack.com/archives/C074M3N3BG9/p1750340995899389)
  hybrid offers only seen by hybrid users.
  offprem offers only seen by offprem and hybrid users.
  onprem offers only seen by onprem and hybrid users.
*/
export const isUserRoleEligibleForOffer = (
  user: UserDataInterfaceWithId,
  offer: OffersInterfaceWithId | OffersInterface,
): boolean => {
  if(user.role === distributorRole
    && user.subRole === hybridRole
    && (offer.jobTypes.includes(hybridRole)
      || offer.jobTypes.includes(onpremiseRole)
      || offer.jobTypes.includes(offpremiseRole)
    )
  ) {
    return true;
  } else if(user.role === distributorRole && user.subRole && offer.jobTypes.includes(user.subRole)) {
    return true;
  }

  return offer.jobTypes.includes(user.role);
}

export const isUserEligibleForOffer = (
  user: UserDataInterfaceWithId,
  offer: OffersInterfaceWithId | OffersInterface,
): boolean => {
  const roleMatched = isUserRoleEligibleForOffer(user, offer);
  const dispensaryMatched = user?.dispensaryId && offer?.dispensaries?.includes(user?.dispensaryId) || false;

  return roleMatched && dispensaryMatched;
}

const uppercaseFirstLetter = (value: string) => {
  return value ? value[0].toUpperCase() + value.substring(1) : '';
}

export function getRoleNameToDisplay(
  role: string,
  subRole: typeof onpremiseRole | typeof offpremiseRole | typeof hybridRole | null | string,
): string {
  const mainRole = uppercaseFirstLetter(role);
  if (subRole) {
    if (subRole === onpremiseRole) {
      return 'On Premise Rep';
    } else if (subRole === offpremiseRole) {
      return 'Off Premise Rep';
    } else if (subRole === hybridRole) {
      return 'Hybrid Rep';
    }

    return mainRole + ' - ' + uppercaseFirstLetter(subRole);
  } else if (role === onpremiseRole) {
    return 'On Premise Rep';
  } else if (role === offpremiseRole) {
    return 'Off Premise Rep';
  } else if (role === hybridRole) {
    return 'Hybrid Rep';
  } else if (role === corporateAssociateRole) {
    return 'Corporate Associate';
  }

  return mainRole;
}

export function offerTypeToDisplay(offerType: OfferType): string {
  switch (offerType) {
    case 'video':
      return 'Video';
    case 'survey':
      return 'Survey';
    case 'teamContest':
      return 'Team Contest';
    case 'dollarContest':
      return 'Dollar Contest';
    case 'skuContest':
      return 'SKU Contest';
    case 'quiz':
      return 'Quiz';
    case 'carousel':
      return 'Carousel';
    case 'employeeVerification':
      return 'Employee Verification';
    case 'rewards':
      return 'Rewards';
    case 'sellSheet':
      return 'Sell Sheet';
    default:
      return '';
  }
}
